<script setup lang="ts" generic="T">
import CustomButton from '@/components/CustomButton.vue';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { capitalizeFirstLetter } from '@/utils/text';
import { computed, ref, useSlots } from 'vue';
import type { VDataTable } from 'vuetify/components';
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs';
import type { Density } from 'vuetify/lib/composables/density.mjs';

const globalStore = useGlobalStore();
const authStore = useAuthStore();
const slots = useSlots();

type ListItem = T & object;

type ListProps<ListItem> = {
    title?: string;
    headers: VDataTable['$props']['headers'];
    items: ListItem[];
    search?: string | null;
    showSearch?: boolean;
    showSelect?: boolean;
    itemsPerPage?: number;
    hideDefaultFooter?: boolean;
    density?: Density;
    sortBy?: SortItem[];
    createButtonCallback?: Function;
    entityName: string;
    nameProperty?: string;
    isInitializing?: boolean;
    // TODO: split all action related props to one seperate actions object
    isDeletable?: boolean;
    isEditable?: boolean;
    editEnabled?: (item: ListItem) => boolean;
    deleteEnabled?: (item: ListItem) => boolean;
    bulkDeleteEnabled?: () => boolean;
    bulkEditEnabled?: () => boolean;
};

const props = withDefaults(defineProps<ListProps<ListItem>>(), { showSearch: true, search: null });

const emit = defineEmits<{
    (e: 'clickTableRow', item: ListItem): void;
    (e: 'clickTableRowEdit', item: ListItem): void;
    (e: 'deleteConfirmed', item: ListItem): void;
    (e: 'bulkDeleteConfirmed', selectedItems: string[]): void;
    (e: 'bulkEditClicked', selectedItems: string[]): void;
}>();

const selectedItems = ref<string[]>([]);
const searchInput = ref<string>();

const markedItemForDeletion = ref<any | null>();
const isConfirmDeleteDialogOpen = ref(false);

const shouldShowActions = (props.isEditable || props.isDeletable) && !authStore.isReadOnly();
const shouldShowBulkActions = props.showSelect && !authStore.isReadOnly();

const headers = computed(() => {
    let headers = props.headers;
    if (shouldShowActions) {
        return headers?.concat({ title: '', value: 'actions', sortable: false, nowrap: true });
    }

    return headers;
});

const confirmDeleteText = computed(() => {
    let subject: string;
    if (selectedItems.value.length > 0 && !markedItemForDeletion.value) {
        subject = selectedItems.value.length > 1 ? `de ${selectedItems.value.length} geselecteerde actiepunten` : 'het geselecteerde actiepunt';
    } else {
        subject = `${markedItemForDeletion.value[props.nameProperty ?? 'name']}`;
    }

    return `Weet je zeker dat je ${subject} wilt verwijderen?`;
});

function handleDeleteClicked(item: ListItem) {
    markedItemForDeletion.value = item;
    isConfirmDeleteDialogOpen.value = true;
}

function handleBulkDeleteClicked() {
    if (!selectedItems.value || selectedItems.value.length === 0) return;
    isConfirmDeleteDialogOpen.value = true;
}

function handleDeleteConfirmed() {
    if (markedItemForDeletion.value) {
        emit('deleteConfirmed', markedItemForDeletion.value);
    } else {
        const items = selectedItems.value;
        selectedItems.value = [];
        emit('bulkDeleteConfirmed', items);
    }
}

function handleBulkEditClicked() {
    if (!selectedItems.value || selectedItems.value.length === 0) return;
    emit('bulkEditClicked', selectedItems.value);
}
</script>

<template>
    <div>
        <section id="listTitle">
            <v-fade-transition>
                <CustomButton
                    v-if="createButtonCallback && !authStore.isReadOnly()"
                    class="mr-2"
                    @click="createButtonCallback()"
                    :text="`${entityName} aanmaken`"
                />
                <h3 v-if="props.title" transition="fade">{{ capitalizeFirstLetter(props.title) }}</h3>
            </v-fade-transition>
            <div class="pa-0">
                <v-fade-transition>
                    <v-row>
                        <v-col lg="4" md="6" sm="12" cols="12">
                            <v-text-field v-if="props.showSearch" v-model="searchInput" label="Zoeken" placeholder="Typ om te zoeken..." />
                        </v-col>
                    </v-row>
                </v-fade-transition>
                <v-fade-transition>
                    <v-data-table
                        v-show="!isInitializing && !globalStore.isLoading"
                        v-model="selectedItems"
                        v-bind="props"
                        density="comfortable"
                        item-value="_id"
                        select-strategy="all"
                        :search="props.search ?? searchInput"
                        :headers="headers"
                        @click:row="(_event: any, row: any) => emit('clickTableRow', row.item)"
                    >
                        <template #top v-if="shouldShowBulkActions">
                            <v-col class="d-flex align-center justify-md-end pa-1 bg-white-soft">
                                <span class="text-caption font-weight-medium pr-2 text-sm">
                                    {{ selectedItems.length }} geselecteerd van {{ props.items.length }}
                                </span>

                                <v-tooltip v-if="bulkEditEnabled == null || bulkEditEnabled()" text="Geselecteerde items bewerken" location="bottom">
                                    <template #activator="{ props }">
                                        <v-btn
                                            density="compact"
                                            icon="edit"
                                            v-bind="props"
                                            color="alt"
                                            variant="tonal"
                                            class="mb-1"
                                            :disabled="selectedItems.length === 0"
                                            @click="handleBulkEditClicked"
                                        />
                                    </template>
                                </v-tooltip>

                                <v-tooltip
                                    v-if="bulkDeleteEnabled == null || bulkDeleteEnabled()"
                                    text="Alle geselecteerde items verwijderen"
                                    location="bottom"
                                >
                                    <template #activator="{ props }">
                                        <v-btn
                                            density="compact"
                                            icon="delete"
                                            v-bind="props"
                                            variant="tonal"
                                            color="red"
                                            class="mb-1"
                                            :disabled="selectedItems.length === 0"
                                            @click="handleBulkDeleteClicked"
                                        />
                                    </template>
                                </v-tooltip>
                            </v-col>
                        </template>

                        <template v-for="(_slot, name) in slots" #[name]="slot">
                            <slot :name="name" v-bind="slot"></slot>
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <div class="d-flex ga-2">
                                <v-tooltip
                                    v-if="isEditable && (editEnabled == null || editEnabled(item))"
                                    location="bottom"
                                    :text="`${entityName} bewerken`"
                                    v-slot:activator="{ props }"
                                >
                                    <v-btn
                                        @click.stop="emit('clickTableRowEdit', item)"
                                        density="compact"
                                        color="alt"
                                        variant="tonal"
                                        v-bind="props"
                                        icon="edit"
                                    />
                                </v-tooltip>
                                <v-tooltip
                                    v-if="isDeletable && (deleteEnabled == null || deleteEnabled(item))"
                                    location="bottom"
                                    :text="`${entityName} verwijderen`"
                                    v-slot:activator="{ props }"
                                >
                                    <v-btn
                                        @click.stop="handleDeleteClicked(item)"
                                        density="compact"
                                        color="red"
                                        variant="tonal"
                                        v-bind="props"
                                        icon="delete"
                                    />
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-fade-transition>
            </div>
        </section>
        <ConfirmDialog
            v-if="isConfirmDeleteDialogOpen"
            @continue="handleDeleteConfirmed"
            @cancel="markedItemForDeletion = null"
            v-model="isConfirmDeleteDialogOpen"
            :text="confirmDeleteText"
        />
    </div>
</template>
